import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { api } from '@/lib/api';

interface ProductionFormData {
  title: string;
  description: string;
  productionDate: string;
  location: string;
}

export default function ProductionNew() {
  const navigate = useNavigate();
  const [error, setError] = useState('');

  const [formData, setFormData] = useState<ProductionFormData>({
    title: '',
    description: '',
    productionDate: '',
    location: '',
  });

  const createMutation = useMutation({
    mutationFn: (data: ProductionFormData) => api.post<{ production: { id: string } }>('/productions', data),
    onSuccess: (response) => {
      navigate(`/productions/${response.data.production.id}`);
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to create production');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }

    if (!formData.productionDate) {
      setError('Production date is required');
      return;
    }

    createMutation.mutate(formData);
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>New Production</h1>
      </div>

      <div className="card">
        <form onSubmit={handleSubmit} className="form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-section">
            <h3>Production Details</h3>

            <div className="form-group">
              <label htmlFor="title">Title *</label>
              <input
                type="text"
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                placeholder="Production title"
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of the production"
                rows={3}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="productionDate">Production Date *</label>
                <input
                  type="date"
                  id="productionDate"
                  value={formData.productionDate}
                  onChange={(e) => setFormData({ ...formData, productionDate: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="location">Location</label>
                <input
                  type="text"
                  id="location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Filming location"
                />
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate('/productions')}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? 'Creating...' : 'Create Production'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
